(function () {
  const sectionSelector = '[data-fill-product-section]';
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    'summary'
  ].join(',');

  const controllers = new WeakMap();

  function getFocusable(container) {
    return Array.from(container.querySelectorAll(focusableSelector)).filter((element) => {
      return element instanceof HTMLElement && (element.offsetParent !== null || element === document.activeElement);
    });
  }

  function parseVariants(section) {
    const script = section.querySelector('[data-fill-product-variants]');
    if (!script) return [];

    try {
      return JSON.parse(script.textContent || '[]');
    } catch (error) {
      console.warn('[fill-product] Variant data could not be parsed.', error);
      return [];
    }
  }

  function parseBundleConfig(section) {
    const script = section.querySelector('[data-fill-product-bundle-components]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.warn('[fill-product] Bundle component data could not be parsed.', error);
      return null;
    }
  }

  function normalizeOptionValue(value) {
    return String(value ?? '').trim().toLocaleLowerCase();
  }

  function bundleValueMatchesComponentValue(bundleValue, componentValue) {
    const bundle = normalizeOptionValue(bundleValue);
    const component = normalizeOptionValue(componentValue);
    return Boolean(bundle && component && (bundle === component || bundle.startsWith(`${component}-`)));
  }

  function getControlValues(group) {
    const controls = Array.from(group.querySelectorAll('[data-fill-product-option]'));
    const values = controls.flatMap((control) => {
      if (control instanceof HTMLSelectElement) return Array.from(control.options).map((option) => option.value);
      return [control.value];
    });
    return Array.from(new Set(values.filter(Boolean)));
  }

  function formatCents(cents, precision = 2, thousands = ',', decimal = '.') {
    const amount = (Number(cents) / 100).toFixed(precision);
    const [whole, fraction] = amount.split('.');
    const wholeWithSeparators = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return precision > 0 ? `${wholeWithSeparators}${decimal}${fraction}` : wholeWithSeparators;
  }

  function formatMoney(format, cents) {
    const moneyFormat = format || '${{amount}}';
    const placeholderMatch = moneyFormat.match(/\{\{\s*(amount(?:_[a-z_]+)?)\s*\}\}/);
    const placeholder = placeholderMatch?.[0] || '{{amount}}';
    const placeholderName = placeholderMatch?.[1] || 'amount';
    let value;

    switch (placeholderName) {
      case 'amount_no_decimals':
        value = formatCents(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatCents(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatCents(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatCents(cents, 0, ' ');
        break;
      case 'amount_with_space_separator':
        value = formatCents(cents, 2, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatCents(cents, 2, "'", '.');
        break;
      default:
        value = formatCents(cents);
    }

    return moneyFormat.replace(placeholder, value);
  }

  function getCartItemsSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
      .filter(Boolean);
  }

  function getCartErrorMessage(error) {
    const data = error?.data || error;
    if (typeof data?.description === 'string' && data.description.trim()) return data.description;
    if (typeof data?.message === 'string' && data.message.trim()) return data.message;
    if (typeof data?.error === 'string' && data.error.trim()) return data.error;
    if (typeof error?.message === 'string' && error.message.trim()) return error.message;
    return "We couldn't add this look. Please try again.";
  }

  async function parseCartResponse(response) {
    const text = await response.text();
    if (!text) return {};

    try {
      return JSON.parse(text);
    } catch {
      if (!response.ok) throw new Error("We couldn't add this look. Please try again.");
      return {};
    }
  }

  function variantMatchesSelection(variant, selectedOptions) {
    return selectedOptions.every((value, index) => !value || variant.options?.[index] === value);
  }

  function hasPossibleVariant(variants, selectedOptions, position, value) {
    const testOptions = selectedOptions.slice();
    testOptions[position] = value;
    return variants.some((variant) => variantMatchesSelection(variant, testOptions));
  }

  function hasAvailableVariant(variants, selectedOptions, position, value) {
    const testOptions = selectedOptions.slice();
    testOptions[position] = value;
    return variants.some((variant) => variant.available === true && variantMatchesSelection(variant, testOptions));
  }

  function isVariantPurchasable(variant) {
    const sharedRule = window.FillProductBundleState?.isVariantPurchasable;
    if (typeof sharedRule === 'function') return sharedRule(variant);
    const variantId = Number(variant?.id);
    return Boolean(Number.isFinite(variantId) && variantId > 0 && variant.available === true);
  }

  function isFullySoldOutComponent(component) {
    const sharedRule = window.FillProductBundleState?.isFullySoldOut;
    if (typeof sharedRule === 'function') return sharedRule(component);
    const variants = Array.isArray(component?.variants) ? component.variants : [];
    return variants.length > 0 && !variants.some(isVariantPurchasable);
  }

  class FillProduct {
    constructor(section) {
      this.section = section;
      this.variants = parseVariants(section);
      this.bundleConfig = parseBundleConfig(section);
      this.bundleComponents = Array.isArray(this.bundleConfig?.components) ? this.bundleConfig.components : [];
      this.isCuratedBundleConfigurator = section.querySelector('[data-fill-product-bundle-card]') !== null;
      this.requestSizeEnabled = section.dataset.fillProductRequestSizeEnabled === 'true'
        && !this.isCuratedBundleConfigurator
        && section.querySelector('[data-fill-product-request-size-trigger]') !== null
        && section.querySelector('[data-fill-product-request-size-modal]') !== null;
      this.requestSizeInitialized = false;
      this.removedComponents = new Set();
      this.componentBindings = new Map();
      this.bundleComponentDataComplete = true;
      this.bundleComponentDataWarningShown = false;
      this.bundleComponentState = null;
      this.bundlePriceMismatch = null;
      this.partialPriceText = '';
      this.selectedOptions = [];
      this.currentVariant = null;
      this.variantResolutionError = false;
      this.currentMediaIndex = 0;
      this.lastFocus = null;
      this.scrollLock = null;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.collectInitialOptions();
      this.initializeBundleComponentBindings();
      this.bind();
      this.setInitialMedia();
      this.updateFromSelection({ updateHistory: false });
      this.observeStickyBar();
      this.enableProductFormInput();
      this.initializeRequestSize();
    }

    destroy() {
      this.syncBundleSubmitRouting({ forceNative: true });
      this.abortController.abort();
      this.unlockPageScroll();
      document.body.classList.remove('fill-product-modal-open');
      controllers.delete(this.section);
    }

    bind() {
      this.section.addEventListener('change', this.handleChange, { signal: this.signal });
      this.section.addEventListener('click', this.handleClick, { signal: this.signal });
      this.section.addEventListener('keydown', this.handleKeydown, { signal: this.signal });
      this.section.addEventListener('submit', this.handleRequestSizeSubmit, { capture: true, signal: this.signal });
      this.section.addEventListener('submit', this.handlePartialSubmit, { capture: true, signal: this.signal });
      this.section.addEventListener('submit', this.handleSubmit, { signal: this.signal });
      this.section.addEventListener('touchstart', this.handleTouchStart, { passive: true, signal: this.signal });
      this.section.addEventListener('touchend', this.handleTouchEnd, { passive: true, signal: this.signal });
      document.addEventListener('shopify:cart:lines-update', this.handleCartLinesUpdate, { signal: this.signal });
    }

    collectInitialOptions() {
      const optionCount = this.variants.reduce((count, variant) => {
        return Math.max(count, variant.options?.length || 0);
      }, 0);

      // Shopify's variant.options array is positional (option1/option2/option3).
      // The Curated Bundle renderer may place those controls in a different visual
      // order and may also render fixed components between configurable ones.
      this.selectedOptions = Array(optionCount).fill('');

      this.optionGroups.forEach((group) => {
        const position = this.getOptionPosition(group);
        if (position === null) return;

        const value = (() => {
          const checked = group.querySelector('[data-fill-product-option]:checked');
          if (checked instanceof HTMLInputElement) return checked.value;

          const select = group.querySelector('select[data-fill-product-option]');
          if (select instanceof HTMLSelectElement) return select.value;

          return '';
        })();

        this.selectedOptions[position] = value;
      });
    }

    get optionGroups() {
      return Array.from(this.section.querySelectorAll('[data-fill-product-option-group]'));
    }

    getOptionPosition(element) {
      const position = Number.parseInt(element?.dataset?.optionPosition || '', 10) - 1;
      return Number.isInteger(position) && position >= 0 ? position : null;
    }

    get isPartialLook() {
      return this.bundleComponents.length > 0 && (
        this.removedComponents.size > 0 ||
        this.automaticallyExcludedComponents.size > 0 ||
        this.nativeParentRequiresPartialFallback
      );
    }

    get nativeParentRequiresPartialFallback() {
      if (!this.isCuratedBundleConfigurator || this.currentVariant?.available !== false) return false;
      return this.bundleComponents.some((component) => !isFullySoldOutComponent(component));
    }

    get automaticallyExcludedComponents() {
      return new Set(
        this.bundleComponents
          .filter(isFullySoldOutComponent)
          .map((component) => Number(component.index))
      );
    }

    get includedBundleComponents() {
      const automaticallyExcluded = this.automaticallyExcludedComponents;
      return this.bundleComponents.filter((component) => {
        const componentIndex = Number(component.index);
        return !this.removedComponents.has(componentIndex) && !automaticallyExcluded.has(componentIndex);
      });
    }

    initializeBundleComponentBindings() {
      if (!this.bundleComponents.length) return;

      const usedOptionIndexes = new Map(
        this.bundleComponents.map((component) => [Number(component.index), new Set()])
      );

      const bindGroup = (group, component) => {
        if (!component || this.componentBindings.has(group)) return false;
        const position = this.getOptionPosition(group);
        if (position === null) return false;

        const componentIndex = Number(component.index);
        const optionIndex = this.findBestComponentOptionIndex(
          group,
          component,
          usedOptionIndexes.get(componentIndex) || new Set()
        );
        if (optionIndex === null) return false;

        usedOptionIndexes.get(componentIndex)?.add(optionIndex);
        this.componentBindings.set(group, { componentIndex, optionIndex, position });
        group.setAttribute('data-fill-product-bundle-component', '');
        group.dataset.fillProductComponentIndex = String(componentIndex);
        group.dataset.fillProductComponentProductId = String(component.id);
        return true;
      };

      this.optionGroups.forEach((group) => {
        const componentIndex = Number.parseInt(group.dataset.fillProductComponentIndex || '', 10);
        if (!Number.isInteger(componentIndex)) return;
        bindGroup(group, this.bundleComponents.find((component) => Number(component.index) === componentIndex));
      });

      this.optionGroups.forEach((group) => {
        if (this.componentBindings.has(group)) return;

        const candidates = this.bundleComponents
          .map((component, componentOrder) => ({
            component,
            componentOrder,
            hasExistingBinding: this.getBindingsForComponent(component).length > 0,
            score: this.getComponentBindingScore(
              group,
              component,
              usedOptionIndexes.get(Number(component.index)) || new Set()
            ),
          }))
          .filter((candidate) => candidate.score > 0)
          .sort((a, b) => {
            if (a.hasExistingBinding !== b.hasExistingBinding) return Number(b.hasExistingBinding) - Number(a.hasExistingBinding);
            if (a.score !== b.score) return b.score - a.score;
            return a.componentOrder - b.componentOrder;
          });

        if (candidates[0]) bindGroup(group, candidates[0].component);
      });

      this.reconcileBundleComponentRows();
      this.updateBundleComponentUi();
    }

    reconcileBundleComponentRows() {
      // Liquid already renders every component card. This step only places each
      // configurable parent option inside its matching child card.
      this.bundleComponents.forEach((component) => {
        const componentIndex = Number(component.index);
        const groups = this.optionGroups.filter((group) => {
          return this.componentBindings.get(group)?.componentIndex === componentIndex;
        });
        if (!groups.length) return;

        const componentProductId = String(component.id);
        const card = this.section.querySelector(
          `[data-fill-product-bundle-card]` +
          `[data-fill-product-component-index="${componentIndex}"]` +
          `[data-fill-product-component-product-id="${componentProductId}"]`
        );
        if (card instanceof HTMLElement) {
          let options = card.querySelector('.fill-product__bundle-card-options');
          if (!(options instanceof HTMLElement)) {
            options = document.createElement('div');
            options.className = 'fill-product__bundle-card-options';
            card.querySelector('.fill-product__bundle-card-content')?.append(options);
          }
          groups.forEach((group) => options.append(group));
          const status = card.querySelector('[data-fill-product-component-status]');
          if (status instanceof HTMLElement) {
            status.dataset.fillProductComponentFixed = 'false';
            const soldOut = isFullySoldOutComponent(component);
            status.textContent = soldOut ? 'SOLD OUT' : '';
            status.classList.toggle('fill-product__bundle-card-status', soldOut);
            status.hidden = !soldOut;
          }
          return;
        }
      });

      const optionPool = this.section.querySelector('[data-fill-product-bundle-option-pool]');
      if (optionPool instanceof HTMLElement) {
        optionPool.hidden = !this.optionGroups.some((group) => !this.componentBindings.has(group));
      }
    }

    findBestComponentOptionIndex(group, component, usedOptionIndexes) {
      const groupValues = getControlValues(group);
      let best = null;

      (component.options || []).forEach((option, optionIndex) => {
        if (usedOptionIndexes.has(optionIndex)) return;
        const optionValues = option.values || [];
        if (!groupValues.length || !optionValues.length) return;

        const matchedGroupValues = groupValues.filter((groupValue) => {
          return optionValues.some((optionValue) => bundleValueMatchesComponentValue(groupValue, optionValue));
        }).length;
        const matchedOptionValues = optionValues.filter((optionValue) => {
          return groupValues.some((groupValue) => bundleValueMatchesComponentValue(groupValue, optionValue));
        }).length;
        const groupCoverage = matchedGroupValues / groupValues.length;
        const optionCoverage = matchedOptionValues / optionValues.length;
        const score = groupCoverage + optionCoverage;

        if (!best || score > best.score) best = { optionIndex, score, groupCoverage, optionCoverage };
      });

      // A group can belong to a child only when every native parent value maps to
      // that child's option values. Partial overlap is ambiguous and must not bind.
      return best && best.groupCoverage === 1 ? best.optionIndex : null;
    }

    getComponentBindingScore(group, component, usedOptionIndexes) {
      const optionIndex = this.findBestComponentOptionIndex(group, component, usedOptionIndexes);
      if (optionIndex === null) return 0;

      const groupValues = getControlValues(group);
      const optionValues = component.options?.[optionIndex]?.values || [];
      const matchedGroupValues = groupValues.filter((groupValue) => {
        return optionValues.some((optionValue) => bundleValueMatchesComponentValue(groupValue, optionValue));
      }).length;
      const matchedOptionValues = optionValues.filter((optionValue) => {
        return groupValues.some((groupValue) => bundleValueMatchesComponentValue(groupValue, optionValue));
      }).length;

      return matchedGroupValues / Math.max(groupValues.length, 1) + matchedOptionValues / Math.max(optionValues.length, 1);
    }

    getBindingsForComponent(component) {
      const componentIndex = Number(component.index);
      return Array.from(this.componentBindings.values()).filter((binding) => binding.componentIndex === componentIndex);
    }

    getSelectedComponentVariant(component) {
      const variants = component.variants || [];
      const bindings = this.getBindingsForComponent(component);

      if (!bindings.length) {
        return variants.find((variant) => String(variant.id) === String(component.selectedVariantId)) ||
          variants.find((variant) => variant.available) ||
          variants[0] || null;
      }

      const matchingVariants = variants.filter((variant) => {
        return bindings.every((binding) => {
          const selectedValue = this.selectedOptions[binding.position];
          return bundleValueMatchesComponentValue(selectedValue, variant.options?.[binding.optionIndex]);
        });
      });

      return matchingVariants.find((variant) => variant.available) || matchingVariants[0] || null;
    }

    getComponentOptionSelectionsValid(component) {
      const bindings = this.getBindingsForComponent(component);
      return bindings.every((binding) => Boolean(this.selectedOptions[binding.position]));
    }

    getBundleComponentState() {
      const derive = window.FillProductBundleState?.derive;
      if (typeof derive !== 'function') {
        return {
          components: [],
          complete: false,
          dataComplete: false,
          availableComponentCount: 0,
          noPurchasableComponents: false,
          componentTotal: null,
          includedTotal: 0,
          mismatch: null,
        };
      }

      return derive({
        components: this.bundleComponents.map((component) => ({
          ...component,
          optionSelectionsValid: this.getComponentOptionSelectionsValid(component),
          variant: this.getSelectedComponentVariant(component),
        })),
        removedIndexes: this.removedComponents,
        nativePrice: this.currentVariant?.price,
      });
    }

    getPartialSelectionState() {
      const components = this.includedBundleComponents.map((component) => ({
        component,
        variant: this.getSelectedComponentVariant(component),
        quantity: Math.max(1, Number(component.quantity) || 1),
      }));
      return {
        components,
        hasComponents: components.length > 0,
        unresolved: components.some(({ variant }) => !variant?.id),
        unavailable: components.some(({ variant }) => variant?.id && !variant.available),
        valid: components.length > 0 && components.every(({ variant, quantity }) => {
          return isVariantPurchasable(variant) && Number.isInteger(quantity) && quantity > 0;
        }),
      };
    }

    toggleBundleComponent(componentIndex, control) {
      if (!this.bundleComponentDataComplete) return;
      if (this.automaticallyExcludedComponents.has(componentIndex)) return;
      const isRemoved = this.removedComponents.has(componentIndex);
      if (!isRemoved && this.includedBundleComponents.length <= 1) return;

      if (isRemoved) this.removedComponents.delete(componentIndex);
      else this.removedComponents.add(componentIndex);

      this.updateBundleComponentUi();
      this.updateFromSelection({ updateHistory: false });

      const nextControl = this.section.querySelector(
        `[data-fill-product-component-toggle][data-fill-product-component-index="${componentIndex}"]`
      );
      (nextControl || control)?.focus?.({ preventScroll: true });
    }

    updateBundleComponentUi() {
      if (!this.bundleComponents.length) return;
      const includedCount = this.includedBundleComponents.length;
      const automaticallyExcluded = this.automaticallyExcludedComponents;

      this.bundleComponents.forEach((component) => {
        const componentIndex = Number(component.index);
        const removed = this.removedComponents.has(componentIndex);
        const soldOut = automaticallyExcluded.has(componentIndex);
        const selector = `[data-fill-product-bundle-component][data-fill-product-component-index="${componentIndex}"]`;

        this.section.querySelectorAll(selector).forEach((element) => {
          element.classList.toggle('is-removed', removed);
          element.classList.toggle('is-sold-out', soldOut);
        });

        this.section
          .querySelectorAll(`[data-fill-product-component-status][data-fill-product-component-index="${componentIndex}"]`)
          .forEach((status) => {
            if (!(status instanceof HTMLElement)) return;
            const fixed = status.dataset.fillProductComponentFixed === 'true';
            status.textContent = soldOut ? 'SOLD OUT' : fixed ? 'One size · included' : '';
            status.classList.toggle('fill-product__bundle-card-status', soldOut);
            status.hidden = !soldOut && !fixed;
          });

        this.section
          .querySelectorAll(`[data-fill-product-component-toggle][data-fill-product-component-index="${componentIndex}"]`)
          .forEach((button) => {
            if (!(button instanceof HTMLButtonElement)) return;
            button.textContent = removed ? 'Add back' : 'Remove';
            button.hidden = soldOut;
            button.disabled = soldOut || !this.bundleComponentDataComplete || (!removed && includedCount <= 1);
            button.setAttribute('aria-pressed', removed ? 'true' : 'false');
            if (soldOut) {
              button.title = 'This piece is sold out.';
            } else if (!this.bundleComponentDataComplete) {
              button.title = 'This Look needs complete component data before individual pieces can be removed.';
            } else {
              button.removeAttribute('title');
            }
            button.setAttribute(
              'aria-label',
              `${removed ? 'Add' : 'Remove'} ${component.title || 'this piece'} ${removed ? 'back to' : 'from'} this look`
            );
          });
      });

      this.syncBundleSubmitRouting();
    }

    syncBundleSubmitRouting({ forceNative = false } = {}) {
      const productForm = this.section.querySelector('product-form-component');
      if (!(productForm instanceof HTMLElement)) return;

      const usePartialSubmit = !forceNative && this.isPartialLook;
      if (usePartialSubmit) {
        // Shopify delegates on:submit from document capture, before this section's
        // capture listener can cancel the parent bundle request. Disable only that
        // declarative hook while a reduced Look is active, then restore it verbatim.
        const nativeSubmitHandler = productForm.getAttribute('on:submit');
        if (nativeSubmitHandler) productForm.dataset.fillProductNativeSubmitHandler = nativeSubmitHandler;
        productForm.removeAttribute('on:submit');
        this.section.dataset.fillProductSubmitMode = 'partial';
        return;
      }

      const nativeSubmitHandler = productForm.dataset.fillProductNativeSubmitHandler;
      if (!productForm.hasAttribute('on:submit') && nativeSubmitHandler) {
        productForm.setAttribute('on:submit', nativeSubmitHandler);
      }
      delete productForm.dataset.fillProductNativeSubmitHandler;
      delete this.section.dataset.fillProductSubmitMode;
    }

    get slides() {
      return Array.from(this.section.querySelectorAll('[data-fill-product-slide]'));
    }

    get activeSlide() {
      return this.slides[this.currentMediaIndex] || null;
    }

    enableProductFormInput() {
      const input = this.section.querySelector('[data-fill-product-variant-input]');
      if (input instanceof HTMLInputElement) {
        input.disabled = false;
      }
    }

    handleChange = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
      if (!target.matches('[data-fill-product-option]')) return;

      const position = this.getOptionPosition(target);
      if (position === null) {
        console.warn('[fill-product] Option control is missing a valid Shopify option position.', target);
        return;
      }

      this.selectedOptions[position] = target.value;
      this.updateFromSelection({ updateHistory: true });
    };

    handleClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const componentToggle = target.closest('[data-fill-product-component-toggle]');
      if (componentToggle instanceof HTMLButtonElement) {
        const componentIndex = Number.parseInt(componentToggle.dataset.fillProductComponentIndex || '', 10);
        if (Number.isInteger(componentIndex)) this.toggleBundleComponent(componentIndex, componentToggle);
        return;
      }

      const thumb = target.closest('[data-fill-product-thumb]');
      if (thumb instanceof HTMLElement) {
        this.showMediaById(thumb.dataset.mediaId);
        return;
      }

      if (target.closest('[data-fill-product-prev]')) {
        this.showMediaByIndex(this.currentMediaIndex - 1);
        return;
      }

      if (target.closest('[data-fill-product-next]')) {
        this.showMediaByIndex(this.currentMediaIndex + 1);
        return;
      }

      const zoomTrigger = target.closest('[data-fill-product-zoom-trigger]');
      if (zoomTrigger instanceof HTMLButtonElement && !zoomTrigger.disabled) {
        const movedX = typeof this.pointerStartX === 'number' ? Math.abs((event.clientX || 0) - this.pointerStartX) : 0;
        if (movedX < 12) this.openZoom(zoomTrigger);
        return;
      }

      if (target.closest('[data-fill-product-zoom-close]')) {
        this.closeZoom();
        return;
      }

      if (target.closest('[data-fill-product-zoom-prev]')) {
        this.showZoomNeighbor(-1);
        return;
      }

      if (target.closest('[data-fill-product-zoom-next]')) {
        this.showZoomNeighbor(1);
        return;
      }

      if (target.closest('[data-fill-product-size-guide-trigger]')) {
        this.openModal('[data-fill-product-size-guide-modal]');
        return;
      }

      if (target.closest('[data-fill-product-size-guide-close]')) {
        this.closeModal('[data-fill-product-size-guide-modal]');
        return;
      }

      if (target.closest('[data-fill-product-request-size-trigger]')) {
        this.openRequestSizeModal();
        return;
      }

      if (target.closest('[data-fill-product-request-size-close]')) {
        this.closeRequestSizeModal();
        return;
      }

      if (target.closest('[data-fill-product-sticky-add]')) {
        const addButton = this.section.querySelector('[data-fill-product-add-button]');
        if (addButton instanceof HTMLButtonElement && !addButton.disabled) addButton.click();
      }
    };

    handleKeydown = (event) => {
      const zoom = this.section.querySelector('[data-fill-product-zoom]');
      const sizeGuide = this.section.querySelector('[data-fill-product-size-guide-modal]');
      const requestSize = this.section.querySelector('[data-fill-product-request-size-modal]');
      const activeDialog = [zoom, sizeGuide, requestSize].find((dialog) => dialog instanceof HTMLElement && !dialog.hidden);

      if (event.key === 'Escape') {
        if (requestSize instanceof HTMLElement && !requestSize.hidden) {
          this.closeRequestSizeModal();
          return;
        }
        if (zoom instanceof HTMLElement && !zoom.hidden) this.closeZoom();
        if (sizeGuide instanceof HTMLElement && !sizeGuide.hidden) this.closeModal('[data-fill-product-size-guide-modal]');
        return;
      }

      if (event.key === 'ArrowLeft' && zoom instanceof HTMLElement && !zoom.hidden) {
        event.preventDefault();
        this.showZoomNeighbor(-1);
      }

      if (event.key === 'ArrowRight' && zoom instanceof HTMLElement && !zoom.hidden) {
        event.preventDefault();
        this.showZoomNeighbor(1);
      }

      if (event.key === 'Tab' && activeDialog instanceof HTMLElement) {
        this.trapFocus(event, activeDialog);
      }
    };

    initializeRequestSize() {
      if (!this.requestSizeEnabled) return;

      const modal = this.section.querySelector('[data-fill-product-request-size-modal]');
      if (!(modal instanceof HTMLElement)) return;

      const state = modal.dataset.fillProductRequestSizeState;
      if (state === 'success' || state === 'error') {
        this.openRequestSizeModal({ captureFocus: false, focusResult: true });
      }
      if (state === 'success') this.clearRequestSizeSuccessUrl();
      this.requestSizeInitialized = true;
    }

    handleRequestSizeSubmit = (event) => {
      if (!this.requestSizeEnabled) return;

      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-fill-product-request-size-form]')) return;

      if (!form.checkValidity()) {
        event.preventDefault();
        form.reportValidity();
        return;
      }

      if (form.dataset.fillProductRequestSubmitting === 'true') {
        event.preventDefault();
        return;
      }

      this.updateRequestSizeContext({ force: true });
      form.dataset.fillProductRequestSubmitting = 'true';
      const submit = form.querySelector('[data-fill-product-request-size-submit]');
      if (submit instanceof HTMLButtonElement) {
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
      }
    };

    handlePartialSubmit = (event) => {
      if (!this.isPartialLook) return;

      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const addButton = this.section.querySelector('[data-fill-product-add-button]');
      if (!(addButton instanceof HTMLButtonElement) || !form.contains(addButton)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      void this.submitPartialLook(addButton);
    };

    async submitPartialLook(addButton) {
      const selectionState = this.getPartialSelectionState();
      if (!selectionState.valid) {
        this.updateAvailability();
        this.updateAddButton();
        return;
      }

      this.syncQuantityToForm();
      const visibleQuantity = this.section.querySelector('quantity-selector-component input[ref="quantityInput"]');
      const lookQuantity = Math.max(
        1,
        Number.parseInt(visibleQuantity instanceof HTMLInputElement ? visibleQuantity.value : '1', 10) || 1
      );
      const look = this.bundleConfig?.look || {};
      const properties = {
        _look_id: `look-${look.id || look.handle}`,
        _look_name: look.title || 'Shop the Look',
        _look_parent: look.handle || String(look.id || ''),
        _look_image: look.image || '',
        _look_label: look.label || 'Complete Look',
      };
      const items = selectionState.components.map(({ variant, quantity }) => ({
        id: Number(variant.id),
        quantity: quantity * lookQuantity,
        properties,
      }));

      if (items.some((item) => !Number.isFinite(item.id) || item.id <= 0 || !Number.isInteger(item.quantity) || item.quantity < 1)) {
        this.updateAvailability();
        this.updateAddButton();
        return;
      }

      const { CartErrorEvent, CartLinesUpdateEvent } = await import('@shopify/events');
      const deferredEvent = CartLinesUpdateEvent.createPromise();
      const eventDetail = {
        source: 'fill-product-partial-look',
        sourceId: this.section.id,
        productId: look.id,
        itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
      };

      this.section.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
          promise: deferredEvent.promise,
          detail: eventDetail,
        })
      );

      addButton.dataset.fillProductState = 'loading';
      addButton.setAttribute('aria-busy', 'true');

      try {
        const sectionIds = getCartItemsSectionIds();
        const payload = { items };
        if (sectionIds.length) payload.sections = sectionIds.join(',');

        const addUrl = window.Theme?.routes?.cart_add_url || this.bundleConfig?.routes?.cartAddUrl || '/cart/add.js';
        const addResponse = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
          credentials: 'same-origin',
        });
        const addData = await parseCartResponse(addResponse);
        if (!addResponse.ok || addData?.status || addData?.errors) {
          const error = new Error(getCartErrorMessage(addData));
          error.data = addData;
          throw error;
        }

        const cartUrl = this.bundleConfig?.routes?.cartUrl || window.Theme?.routes?.cart_url || '/cart';
        const cartResponse = await fetch(`${cartUrl}.json`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (!cartResponse.ok) throw new Error("We couldn't refresh your bag. Please try again.");
        const cart = await cartResponse.json();

        deferredEvent.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            ...eventDetail,
            items: cart.items,
            sections: addData.sections,
            didError: false,
          },
        });
      } catch (error) {
        deferredEvent.reject(error);
        this.section.dispatchEvent(
          new CartErrorEvent({
            error: getCartErrorMessage(error),
            code: 'INVALID',
            detail: error?.data,
          })
        );
        const availability = this.section.querySelector('[data-fill-product-availability]');
        if (availability instanceof HTMLElement) {
          availability.textContent = getCartErrorMessage(error);
          availability.dataset.status = 'error';
        }
      }
    }

    handleSubmit = (event) => {
      if (this.isPartialLook) return;
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const addButton = this.section.querySelector('[data-fill-product-add-button]');
      if (!(addButton instanceof HTMLButtonElement) || !form.contains(addButton)) return;

      this.syncQuantityToForm();

      if (!this.currentVariant || !this.currentVariant.available) {
        event.preventDefault();
        this.updateAddButton();
        return;
      }

      addButton.dataset.fillProductState = 'loading';
      addButton.setAttribute('aria-busy', 'true');
    };

    handleCartLinesUpdate = (event) => {
      const sourceInsideSection = event.target instanceof Element && this.section.contains(event.target);
      if (!sourceInsideSection || event.action !== 'add') return;

      event.promise
        ?.then(({ detail }) => {
          this.clearLoadingState();
          if (detail?.didError) return;
          if (this.section.dataset.afterAddBehavior === 'cart') {
            window.location.href = this.section.dataset.cartUrl || '/cart';
          }
        })
        .catch(() => this.clearLoadingState());
    };

    handleTouchStart = (event) => {
      if (!event.target.closest('[data-fill-product-stage]')) return;
      const touch = event.touches[0];
      if (!touch) return;
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.pointerStartX = touch.clientX;
    };

    handleTouchEnd = (event) => {
      if (!event.target.closest('[data-fill-product-stage]')) return;
      const touch = event.changedTouches[0];
      if (!touch) return;

      const deltaX = touch.clientX - this.touchStartX;
      const deltaY = touch.clientY - this.touchStartY;
      if (Math.abs(deltaX) > 48 && Math.abs(deltaX) > Math.abs(deltaY) * 1.4) {
        this.showMediaByIndex(this.currentMediaIndex + (deltaX < 0 ? 1 : -1));
      }
    };

    setInitialMedia() {
      const active = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      this.currentMediaIndex = active >= 0 ? active : 0;
      this.updateMediaState();
    }

    updateFromSelection({ updateHistory }) {
      this.currentVariant = this.resolveCurrentVariant();
      this.validateBundleComponentCoverage();
      this.syncBundleSubmitRouting();

      if (this.isPartialLook) this.updatePartialOptionStates();
      else this.updateOptionStates();
      this.updateVariantInput();
      this.updateRequestSizeContext();
      this.updatePrice();
      this.updateBundleComponentPrices();
      this.updateAvailability();
      this.updateQuantity();
      this.updateAddButton();
      this.updateVariantMedia();
      this.updateOptionLabels();
      this.updateStickyPrice();

      if (updateHistory && this.currentVariant && this.section.dataset.enableHistory === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', this.currentVariant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    validateBundleComponentCoverage() {
      if (!this.bundleComponents.length) return;

      if (!this.isCuratedBundleConfigurator) {
        if (!this.currentVariant) return;

        const componentVariants = this.bundleComponents.map((component) => this.getSelectedComponentVariant(component));
        if (componentVariants.some((variant) => !variant)) return;

        const componentTotal = componentVariants.reduce((sum, variant, index) => {
          const quantity = Math.max(1, Number(this.bundleComponents[index]?.quantity) || 1);
          return sum + (Number(variant.price) || 0) * quantity;
        }, 0);
        const parentTotal = Number(this.currentVariant.price);
        const complete = Number.isFinite(parentTotal) && componentTotal === parentTotal;

        if (this.bundleComponentDataComplete !== complete) {
          this.bundleComponentDataComplete = complete;
          this.updateBundleComponentUi();
        }

        if (!complete && !this.bundleComponentDataWarningShown) {
          this.bundleComponentDataWarningShown = true;
          console.warn('[fill-product] Component exclusion is disabled because custom.look_components does not match the native bundle total.', {
            look: this.bundleConfig?.look?.handle,
            componentTotal,
            parentTotal,
          });
        }
        return;
      }

      this.bundleComponentState = this.getBundleComponentState();
      const unboundOptionPositions = this.optionGroups
        .filter((group) => !this.componentBindings.has(group))
        .map((group) => this.getOptionPosition(group))
        .filter((position) => position !== null)
        .map((position) => position + 1);
      const complete = this.bundleComponentState.dataComplete && unboundOptionPositions.length === 0;
      this.bundlePriceMismatch = this.bundleComponentState.mismatch;

      if (this.bundleComponentDataComplete !== complete) {
        this.bundleComponentDataComplete = complete;
        this.updateBundleComponentUi();
      }
      this.section.toggleAttribute('data-fill-product-bundle-price-mismatch', Boolean(this.bundlePriceMismatch));

      if (!complete && !this.bundleComponentDataWarningShown) {
        this.bundleComponentDataWarningShown = true;
        console.warn('[fill-product] Component exclusion is disabled because a Curated Bundle option could not be matched to a component.', {
          look: this.bundleConfig?.look?.handle,
          unboundOptionPositions,
        });
      }
    }

    updateBundleComponentPrices() {
      if (!this.bundleComponents.length || !this.section.querySelector('[data-fill-product-bundle-card]')) return;

      const state = this.bundleComponentState || this.getBundleComponentState();
      const moneyFormat = this.bundleConfig?.moneyFormat;
      state.components.forEach((component) => {
        const price = Number.isFinite(component.price) ? formatMoney(moneyFormat, component.price) : '—';
        this.section
          .querySelectorAll(`[data-fill-product-component-price][data-fill-product-component-index="${component.index}"]`)
          .forEach((target) => {
            target.textContent = price;
          });
      });

      const totalTarget = this.section.querySelector('[data-fill-product-bundle-total]');
      if (!(totalTarget instanceof HTMLElement)) return;

      const partialSelection = this.getPartialSelectionState();
      const total = this.isPartialLook
        ? (partialSelection.valid ? state.includedTotal : null)
        : Number(this.currentVariant?.price);
      totalTarget.textContent = Number.isFinite(total) ? formatMoney(moneyFormat, total) : '—';
    }

    updatePartialOptionStates() {
      this.optionGroups.forEach((group) => {
        const binding = this.componentBindings.get(group);
        if (!binding) return;
        const component = this.bundleComponents.find((candidate) => Number(candidate.index) === binding.componentIndex);
        if (!component) return;
        const excluded = this.removedComponents.has(binding.componentIndex) ||
          this.automaticallyExcludedComponents.has(binding.componentIndex);
        const componentBindings = this.getBindingsForComponent(component);

        const valueIsAvailable = (value) => {
          return (component.variants || []).some((variant) => {
            if (!variant.available) return false;
            return componentBindings.every((candidateBinding) => {
              const selectedValue = candidateBinding === binding
                ? value
                : this.selectedOptions[candidateBinding.position];
              return bundleValueMatchesComponentValue(
                selectedValue,
                variant.options?.[candidateBinding.optionIndex]
              );
            });
          });
        };

        group.querySelectorAll('[data-fill-product-option]').forEach((control) => {
          if (control instanceof HTMLInputElement) {
            const unavailable = !excluded && !valueIsAvailable(control.value);
            control.disabled = excluded || unavailable;
            control.closest('.fill-product__option-choice')?.classList.toggle('is-unavailable', unavailable);
            control.setAttribute('aria-disabled', excluded || unavailable ? 'true' : 'false');
          }

          if (control instanceof HTMLSelectElement) {
            control.disabled = excluded;
            Array.from(control.options).forEach((option) => {
              option.disabled = !excluded && !valueIsAvailable(option.value);
            });
          }
        });
      });
    }

    resolveCurrentVariant() {
      this.variantResolutionError = false;
      if (!this.variants.length) return null;

      if (!this.optionGroups.length) {
        const selectedVariantId = this.section.dataset.selectedVariantId;
        return this.variants.find((variant) => String(variant.id) === String(selectedVariantId)) ||
          this.variants.find((variant) => variant.selected) ||
          this.variants.find((variant) => variant.available) ||
          this.variants[0];
      }

      const resolved = this.variants.find((variant) => {
        return variant.options?.every((optionValue, index) => optionValue === this.selectedOptions[index]);
      }) || null;

      const hasCompleteSelection = this.selectedOptions.length > 0 && this.selectedOptions.every(Boolean);
      if (!resolved && hasCompleteSelection) {
        this.variantResolutionError = true;
        const mapping = this.optionGroups.map((group) => {
          const position = this.getOptionPosition(group);
          return {
            label: group.querySelector('.fill-product__option-label')?.textContent?.trim() || '',
            position: position === null ? null : position + 1,
            value: position === null ? '' : this.selectedOptions[position] || '',
          };
        });
        console.warn('[fill-product] No parent variant matched the selected Shopify option positions.', {
          selectedOptions: this.selectedOptions.slice(),
          mapping,
        });
      }

      return resolved;
    }

    updateOptionStates() {
      this.optionGroups.forEach((group) => {
        const position = this.getOptionPosition(group);
        if (position === null) return;
        const isStandaloneSizeGroup = !this.isCuratedBundleConfigurator
          && group.hasAttribute('data-fill-product-size-option-group');

        const controls = group.querySelectorAll('[data-fill-product-option]');
        controls.forEach((control) => {
          const value = control.value;
          const possible = hasPossibleVariant(this.variants, this.selectedOptions, position, value);
          const available = hasAvailableVariant(this.variants, this.selectedOptions, position, value);
          const variantForValue = this.variants.find((variant) => {
            const testOptions = this.selectedOptions.slice();
            testOptions[position] = value;
            return variantMatchesSelection(variant, testOptions);
          });
          const unavailable = possible && !available && variantForValue && !variantForValue.available;
          const disabled = this.isCuratedBundleConfigurator ? !available : !possible;

          if (control instanceof HTMLInputElement) {
            control.disabled = disabled;
            const choice = control.closest('.fill-product__option-choice');
            choice?.classList.toggle('is-unavailable', Boolean(unavailable));
            choice?.classList.toggle('is-sold-out', Boolean(isStandaloneSizeGroup && unavailable));
            if (isStandaloneSizeGroup && unavailable) {
              control.dataset.fillProductOptionState = 'sold-out';
              control.setAttribute('aria-label', `${value}, sold out`);
            } else {
              delete control.dataset.fillProductOptionState;
              control.removeAttribute('aria-label');
            }
            control.setAttribute('aria-disabled', disabled || unavailable ? 'true' : 'false');
          }

          if (control instanceof HTMLSelectElement) {
            Array.from(control.options).forEach((option) => {
              const optionPossible = hasPossibleVariant(this.variants, this.selectedOptions, position, option.value);
              const optionAvailable = hasAvailableVariant(this.variants, this.selectedOptions, position, option.value);
              option.disabled = this.isCuratedBundleConfigurator ? !optionAvailable : !optionPossible;
            });
          }
        });
      });
    }

    updateVariantInput() {
      const input = this.section.querySelector('[data-fill-product-variant-input]');
      if (input instanceof HTMLInputElement) {
        input.value = this.currentVariant ? this.currentVariant.id : '';
      }
    }

    updateRequestSizeContext({ force = false } = {}) {
      if (!this.requestSizeEnabled) return;

      const form = this.section.querySelector('[data-fill-product-request-size-form]');
      const modal = this.section.querySelector('[data-fill-product-request-size-modal]');
      if (!(form instanceof HTMLFormElement) || !(modal instanceof HTMLElement)) return;
      if (!force && !this.requestSizeInitialized && modal.dataset.fillProductRequestSizeState !== 'idle') return;

      const variantId = form.elements.namedItem('contact[Current variant ID]');
      const variantTitle = form.elements.namedItem('contact[Current variant title]');
      if (variantId instanceof HTMLInputElement) variantId.value = this.currentVariant ? String(this.currentVariant.id) : '';
      if (variantTitle instanceof HTMLInputElement) variantTitle.value = this.currentVariant?.title || '';

      const body = form.elements.namedItem('contact[body]');
      if (!(body instanceof HTMLTextAreaElement)) return;

      const value = (name) => {
        const field = form.elements.namedItem(name);
        return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement ? field.value.trim() : '';
      };
      body.value = [
        `Request type: ${value('contact[Request type]')}`,
        `Product: ${value('contact[Product]')}`,
        `Product handle or ID: ${value('contact[Product handle or ID]')}`,
        `Product URL: ${value('contact[Product URL]')}`,
        `Customer email: ${value('contact[email]')}`,
        `Requested size: ${value('contact[Requested size]')}`,
        `Current variant ID: ${value('contact[Current variant ID]')}`,
        `Current variant title: ${value('contact[Current variant title]')}`,
      ].join('\n');
    }

    updatePrice() {
      const current = this.section.querySelector('[data-fill-product-current-price]');
      const compare = this.section.querySelector('[data-fill-product-compare-price]');
      const sale = this.section.querySelector('[data-fill-product-sale-label]');
      const unit = this.section.querySelector('[data-fill-product-unit-price]');
      if (!(current instanceof HTMLElement)) return;

      if (this.isPartialLook) {
        const selectionState = this.getPartialSelectionState();
        const hasResolvedPrices = selectionState.components.every(({ variant }) => Boolean(variant));
        const total = selectionState.components.reduce((sum, { variant }) => sum + (Number(variant?.price) || 0), 0);
        this.partialPriceText = selectionState.hasComponents && hasResolvedPrices
          ? formatMoney(this.bundleConfig?.moneyFormat, total)
          : '';
        current.textContent = this.partialPriceText;
        compare?.setAttribute('hidden', '');
        sale?.setAttribute('hidden', '');
        unit?.setAttribute('hidden', '');
        return;
      }

      this.partialPriceText = '';

      if (!this.currentVariant) {
        current.textContent = '';
        compare?.setAttribute('hidden', '');
        sale?.setAttribute('hidden', '');
        unit?.setAttribute('hidden', '');
        return;
      }

      current.textContent = this.currentVariant.priceText;
      const onSale = Number(this.currentVariant.compareAtPrice) > Number(this.currentVariant.price);

      if (compare instanceof HTMLElement) {
        compare.textContent = onSale ? this.currentVariant.compareAtPriceText || '' : '';
        compare.hidden = !onSale;
      }

      if (sale instanceof HTMLElement) sale.hidden = !onSale;

      if (unit instanceof HTMLElement) {
        unit.textContent = this.currentVariant.unitPriceText || '';
        unit.hidden = !this.currentVariant.unitPriceText;
      }
    }

    updateAvailability() {
      const availability = this.section.querySelector('[data-fill-product-availability]');
      if (!(availability instanceof HTMLElement)) return;

      if (this.isPartialLook) {
        const selectionState = this.getPartialSelectionState();

        if (!selectionState.hasComponents) {
          availability.textContent = 'Sold out';
          availability.dataset.status = 'sold-out';
        } else if (selectionState.unresolved) {
          availability.textContent = 'Select available options';
          availability.dataset.status = 'options-required';
        } else if (selectionState.unavailable) {
          availability.textContent = 'Select available options';
          availability.dataset.status = 'options-required';
        } else {
          availability.textContent = 'In stock';
          availability.dataset.status = 'in-stock';
        }
        return;
      }

      const exact = this.section.dataset.showExactInventory === 'true';
      const threshold = Number.parseInt(this.section.dataset.lowStockThreshold || '6', 10);
      const variant = this.currentVariant;
      const hasIncompleteSelection = this.optionGroups.length > 0 && this.selectedOptions.some((value) => !value);

      if (!variant) {
        if (this.variantResolutionError) {
          availability.textContent = 'Unable to determine availability';
          availability.dataset.status = 'error';
        } else if (hasIncompleteSelection) {
          availability.textContent = 'Select options';
          availability.dataset.status = 'options-required';
        } else {
          availability.textContent = 'Sold out';
          availability.dataset.status = 'sold-out';
        }
        return;
      }

      if (!variant.available) {
        availability.textContent = 'Sold out';
        availability.dataset.status = 'sold-out';
        return;
      }

      if (variant.inventoryManagement === 'shopify' && variant.inventoryPolicy !== 'continue') {
        const quantity = Number(variant.inventoryQuantity);
        if (quantity > 0 && quantity <= threshold) {
          availability.textContent = exact ? `${quantity} in stock` : 'Low stock';
          availability.dataset.status = 'low';
          return;
        }
      }

      availability.textContent = 'In stock';
      availability.dataset.status = 'in-stock';
    }

    updateQuantity() {
      const variant = this.currentVariant;
      const input = this.section.querySelector('quantity-selector-component input[ref="quantityInput"]');
      const hiddenQuantity = this.section.querySelector('[data-fill-product-hidden-quantity]');
      const partialState = this.isPartialLook ? this.getPartialSelectionState() : null;
      const min = this.isPartialLook ? 1 : variant?.quantityRule?.min || 1;
      const increment = this.isPartialLook ? 1 : variant?.quantityRule?.increment || 1;
      const max = this.isPartialLook ? '' : variant?.quantityRule?.max || '';

      if (input instanceof HTMLInputElement) {
        input.min = String(min);
        input.step = String(increment);
        if (max) input.max = String(max);
        else input.removeAttribute('max');
        if (!input.value || Number(input.value) < min) input.value = String(min);
        input.disabled = this.isPartialLook ? !partialState?.valid : !variant || !variant.available;
        const quantitySelector = input.closest('quantity-selector-component');
        if (quantitySelector && typeof quantitySelector.updateConstraints === 'function') {
          quantitySelector.updateConstraints(String(min), max ? String(max) : null, String(increment));
        }
      }

      if (hiddenQuantity instanceof HTMLInputElement) {
        hiddenQuantity.value = input instanceof HTMLInputElement ? input.value || String(min) : String(min);
      }
    }

    updateAddButton() {
      const button = this.section.querySelector('[data-fill-product-add-button]');
      const text = this.section.querySelector('[data-fill-product-add-text]');
      const dynamicCheckout = this.section.querySelector('[data-fill-product-dynamic-checkout]');
      const stickyButton = this.section.querySelector('[data-fill-product-sticky-add]');
      const stickyText = this.section.querySelector('[data-fill-product-sticky-text]');
      if (!(button instanceof HTMLButtonElement)) return;

      if (this.isPartialLook) {
        const selectionState = this.getPartialSelectionState();
        const label = selectionState.valid
          ? 'Add to Bag'
          : selectionState.hasComponents
            ? 'Choose Options'
            : 'Sold Out';
        button.disabled = !selectionState.valid;
        if (text instanceof HTMLElement) text.textContent = label;
        if (dynamicCheckout instanceof HTMLElement) dynamicCheckout.hidden = true;
        if (stickyButton instanceof HTMLButtonElement) stickyButton.disabled = !selectionState.valid;
        if (stickyText instanceof HTMLElement) stickyText.textContent = label;
        return;
      }

      let label = this.variantResolutionError ? 'Unavailable' : 'Sold Out';
      let disabled = true;
      const hasIncompleteSelection = this.optionGroups.length > 0 && this.selectedOptions.some((value) => !value);

      if (!this.variantResolutionError && hasIncompleteSelection) label = 'Choose Options';

      if (this.currentVariant) {
        if (this.currentVariant.available) {
          label = 'Add to Bag';
          disabled = false;
        } else {
          label = 'Sold Out';
        }
      }

      button.disabled = disabled;
      if (text instanceof HTMLElement) text.textContent = label;
      if (dynamicCheckout instanceof HTMLElement) dynamicCheckout.hidden = disabled;

      if (stickyButton instanceof HTMLButtonElement) {
        stickyButton.disabled = disabled;
      }
      if (stickyText instanceof HTMLElement) stickyText.textContent = label;
    }

    syncQuantityToForm() {
      const visibleQuantity = this.section.querySelector('quantity-selector-component input[ref="quantityInput"]');
      const hiddenQuantity = this.section.querySelector('[data-fill-product-hidden-quantity]');
      if (visibleQuantity instanceof HTMLInputElement && hiddenQuantity instanceof HTMLInputElement) {
        hiddenQuantity.value = visibleQuantity.value || visibleQuantity.min || '1';
      }
    }

    updateVariantMedia() {
      const mediaId = this.currentVariant?.featuredMediaId;
      if (mediaId) this.showMediaById(String(mediaId), { silent: true });

      const addContainer = this.section.querySelector('add-to-cart-component[ref="addToCartButtonContainer"]');
      if (addContainer instanceof HTMLElement && this.currentVariant?.featuredMediaImage) {
        addContainer.dataset.productVariantMedia = this.currentVariant.featuredMediaImage;
      }
    }

    updateOptionLabels() {
      this.optionGroups.forEach((group) => {
        const position = this.getOptionPosition(group);
        if (position === null) return;

        const label = group.querySelector('[data-fill-product-option-current]');
        if (label instanceof HTMLElement) label.textContent = this.selectedOptions[position] || '';
      });
    }

    updateStickyPrice() {
      const stickyPrice = this.section.querySelector('[data-fill-product-sticky-price]');
      if (!(stickyPrice instanceof HTMLElement)) return;

      if (this.isPartialLook) {
        stickyPrice.textContent = this.partialPriceText;
      } else if (this.currentVariant) {
        stickyPrice.textContent = this.currentVariant.priceText;
      }
    }

    clearLoadingState() {
      const addButton = this.section.querySelector('[data-fill-product-add-button]');
      if (addButton instanceof HTMLButtonElement) {
        delete addButton.dataset.fillProductState;
        addButton.removeAttribute('aria-busy');
      }
    }

    showMediaById(mediaId, options = {}) {
      const index = this.slides.findIndex((slide) => slide instanceof HTMLElement && slide.dataset.mediaId === String(mediaId));
      if (index >= 0) this.showMediaByIndex(index, options);
    }

    showMediaByIndex(index, options = {}) {
      const slides = this.slides;
      if (!slides.length) return;
      const nextIndex = (index + slides.length) % slides.length;
      this.currentMediaIndex = nextIndex;
      this.updateMediaState();
      if (!options.silent) this.activeSlide?.querySelector('[data-fill-product-zoom-trigger]')?.focus?.();
    }

    updateMediaState() {
      this.slides.forEach((slide, index) => {
        const active = index === this.currentMediaIndex;
        slide.classList.toggle('is-active', active);
        slide.hidden = !active;
      });

      const activeSlide = this.activeSlide;
      const activeMediaId = activeSlide instanceof HTMLElement ? activeSlide.dataset.mediaId : '';
      this.section.querySelectorAll('[data-fill-product-thumb]').forEach((thumb) => {
        const active = thumb instanceof HTMLElement && thumb.dataset.mediaId === activeMediaId;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });

      const counter = this.section.querySelector('[data-fill-product-counter]');
      if (counter instanceof HTMLElement) {
        counter.textContent = `${this.currentMediaIndex + 1} / ${this.slides.length}`;
      }
    }

    openZoom(trigger) {
      const zoom = this.section.querySelector('[data-fill-product-zoom]');
      const image = this.section.querySelector('[data-fill-product-zoom-image]');
      const src = trigger.dataset.zoomSrc;
      if (!(zoom instanceof HTMLElement) || !(image instanceof HTMLImageElement) || !src) return;

      if (zoom.hidden) {
        this.lastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : trigger;
      }
      image.src = src;
      image.alt = trigger.dataset.zoomAlt || '';
      zoom.hidden = false;
      this.lockPageScroll();
      const close = zoom.querySelector('[data-fill-product-zoom-close]');
      if (close instanceof HTMLElement) close.focus();
    }

    closeZoom() {
      const zoom = this.section.querySelector('[data-fill-product-zoom]');
      const image = this.section.querySelector('[data-fill-product-zoom-image]');
      if (!(zoom instanceof HTMLElement)) return;
      zoom.hidden = true;
      if (image instanceof HTMLImageElement) image.removeAttribute('src');
      this.unlockPageScroll();
      this.lastFocus?.focus?.({ preventScroll: true });
    }

    showZoomNeighbor(direction) {
      const imageSlides = this.slides.filter((slide) => slide instanceof HTMLElement && slide.dataset.mediaType === 'image');
      if (!imageSlides.length) return;
      const active = this.activeSlide;
      const currentImageIndex = imageSlides.indexOf(active);
      const nextImage = imageSlides[(currentImageIndex + direction + imageSlides.length) % imageSlides.length];
      if (!(nextImage instanceof HTMLElement)) return;
      this.showMediaById(nextImage.dataset.mediaId, { silent: true });
      const trigger = nextImage.querySelector('[data-fill-product-zoom-trigger]');
      if (trigger instanceof HTMLButtonElement) this.openZoom(trigger);
    }

    openModal(selector) {
      const modal = this.section.querySelector(selector);
      if (!(modal instanceof HTMLElement)) return;

      this.lastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      modal.hidden = false;
      document.body.classList.add('fill-product-modal-open');
      const focusable = getFocusable(modal);
      (focusable[0] || modal).focus();
    }

    closeModal(selector) {
      const modal = this.section.querySelector(selector);
      if (!(modal instanceof HTMLElement)) return;

      modal.hidden = true;
      document.body.classList.remove('fill-product-modal-open');
      this.lastFocus?.focus?.();
    }

    openRequestSizeModal({ captureFocus = true, focusResult = false } = {}) {
      if (!this.requestSizeEnabled) return;

      const modal = this.section.querySelector('[data-fill-product-request-size-modal]');
      if (!(modal instanceof HTMLElement)) return;

      if (captureFocus) {
        this.lastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      }
      modal.hidden = false;
      this.lockPageScroll();

      const preferredFocus = focusResult
        ? modal.querySelector('[data-fill-product-request-size-result]')
        : modal.querySelector('input[name="contact[email]"]');
      if (preferredFocus instanceof HTMLElement) preferredFocus.focus();
      else (getFocusable(modal)[0] || modal).focus();
    }

    closeRequestSizeModal() {
      const modal = this.section.querySelector('[data-fill-product-request-size-modal]');
      if (!(modal instanceof HTMLElement) || modal.hidden) return;

      if (modal.dataset.fillProductRequestSizeState === 'success') {
        this.clearRequestSizeSuccessUrl();
        this.resetRequestSizeSuccessState(modal);
      }
      modal.hidden = true;
      this.unlockPageScroll();
      const returnFocus = this.lastFocus;
      this.lastFocus = null;
      returnFocus?.focus?.({ preventScroll: true });
    }

    clearRequestSizeSuccessUrl() {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('contact_posted')) return;

      url.searchParams.delete('contact_posted');
      const form = this.section.querySelector('[data-fill-product-request-size-form]');
      const formHash = form instanceof HTMLFormElement && form.id ? `#${form.id}` : '';
      if (url.hash === formHash || url.hash === '#contact_form') url.hash = '';
      window.history.replaceState(window.history.state, '', url.toString());
    }

    resetRequestSizeSuccessState(modal) {
      const success = modal.querySelector('[data-fill-product-request-size-success]');
      const content = modal.querySelector('[data-fill-product-request-size-form-content]');
      if (success instanceof HTMLElement) success.hidden = true;
      if (content instanceof HTMLElement) content.hidden = false;
      modal.dataset.fillProductRequestSizeState = 'idle';

      const form = this.section.querySelector('[data-fill-product-request-size-form]');
      if (!(form instanceof HTMLFormElement)) return;

      const requestedSize = form.elements.namedItem('contact[Requested size]');
      if (requestedSize instanceof HTMLInputElement) requestedSize.value = '';
      delete form.dataset.fillProductRequestSubmitting;

      const submit = form.querySelector('[data-fill-product-request-size-submit]');
      if (submit instanceof HTMLButtonElement) {
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
      }
      this.updateRequestSizeContext({ force: true });
    }

    lockPageScroll() {
      if (this.scrollLock) return;

      const root = document.documentElement;
      const body = document.body;
      const scrollY = window.scrollY;
      this.scrollLock = {
        scrollY,
        rootOverflow: root.style.overflow,
        bodyOverflow: body.style.overflow,
        bodyPosition: body.style.position,
        bodyTop: body.style.top,
        bodyWidth: body.style.width
      };

      root.classList.add('fill-product-modal-open');
      body.classList.add('fill-product-modal-open');
      root.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.width = '100%';
    }

    unlockPageScroll() {
      if (!this.scrollLock) return;

      const { scrollY, rootOverflow, bodyOverflow, bodyPosition, bodyTop, bodyWidth } = this.scrollLock;
      const root = document.documentElement;
      const body = document.body;

      root.classList.remove('fill-product-modal-open');
      body.classList.remove('fill-product-modal-open');
      root.style.overflow = rootOverflow;
      body.style.overflow = bodyOverflow;
      body.style.position = bodyPosition;
      body.style.top = bodyTop;
      body.style.width = bodyWidth;
      this.scrollLock = null;

      window.scrollTo(0, scrollY);
    }

    trapFocus(event, container) {
      const focusable = getFocusable(container);
      if (!focusable.length) {
        event.preventDefault();
        container.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    observeStickyBar() {
      const bar = this.section.querySelector('[data-fill-product-sticky-bar]');
      const button = this.section.querySelector('[data-fill-product-add-button]');
      if (!(bar instanceof HTMLElement) || !(button instanceof HTMLElement)) return;
      if (window.Shopify?.designMode) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (window.matchMedia('(min-width: 750px)').matches) {
            bar.hidden = true;
            return;
          }
          const canAdd = this.isPartialLook
            ? this.getPartialSelectionState().valid
            : Boolean(this.currentVariant?.available);
          bar.hidden = entry.isIntersecting || !canAdd;
        },
        { threshold: 0.08 }
      );

      observer.observe(button);
      this.signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    }
  }

  function init(root = document) {
    root.querySelectorAll(sectionSelector).forEach((section) => {
      if (!(section instanceof HTMLElement) || controllers.has(section)) return;
      controllers.set(section, new FillProduct(section));
    });
  }

  function unload(root = document) {
    root.querySelectorAll(sectionSelector).forEach((section) => {
      controllers.get(section)?.destroy();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
})();
